/**
 * GameLogicDiagram — a DETERMINISTIC, data-only picture of a game's logic for
 * the Overview tab. Everything here is read straight from the creation's
 * GameDesign (the mechanics authority) and the active build's asset bindings —
 * no LLM, no inferred entity-to-entity edges (those relationships live only as
 * prose in `mechanics`, so we render them as the contract they are rather than
 * guessing a graph). A live binding wins over the library-name fallback, so
 * the diagram cannot show a stale sprite when an entity's art was rebound.
 *
 * Layout reads top-to-bottom as the game's "flow": what it IS → the entities it
 * models (with art) → the verbs the player performs → the rules → how you win.
 * Each section spans the FULL width; the entities themselves flow in a
 * responsive grid so sprites sit side-by-side instead of in a narrow column.
 */
import type { CreationAsset, GameDesign } from '@djgg/creations';
import { slugify } from '@djgg/creations';
import { Link } from 'react-router-dom';
import { roleForDesignEntity } from './visualUsage.js';

/** Canonical asset-name → public image URL, for the drawable (non-cover) kinds
 *  an entity or background can bind to. */
function spriteIndex(assets: CreationAsset[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const a of assets ?? []) {
    if (a.kind === 'cover' || a.kind === 'palette') continue;
    map.set(slugify(a.name), a.url);
  }
  return map;
}

function EntityNode({
  name,
  role,
  url,
  source,
  assetName,
}: {
  name: string;
  role: string;
  url?: string;
  source: string;
  assetName?: string;
}) {
  return (
    <div className="logic-entity" data-testid="logic-entity">
      <div
        className={`logic-entity__art${url ? ' logic-entity__art--img' : ' logic-entity__art--status'}`}
        style={url ? undefined : { background: 'var(--bg-panel)' }}
      >
        {url ? (
          <img src={url} alt="" loading="lazy" aria-hidden="true" />
        ) : (
          <span aria-hidden="true">—</span>
        )}
      </div>
      <div className="logic-entity__body">
        <div className="logic-entity__name">{name}</div>
        <div className="logic-entity__role">{role}</div>
        {assetName ? (
          <Link className="logic-entity__source" to={`?tab=assets#asset-${assetName}`}>
            {source}
          </Link>
        ) : (
          <div className="logic-entity__source">{source}</div>
        )}
      </div>
    </div>
  );
}

export function GameLogicDiagram({
  design,
  assets,
  liveUsage,
}: {
  design: GameDesign;
  assets: CreationAsset[] | undefined;
  /** The active build's confirmed visual usage. Absence means the game has not
   * stamped a manifest summary yet, so library art is visibly labelled pending
   * confirmation rather than presented as live. */
  liveUsage?: import('@djgg/creations').ManifestVisualUsage[];
}) {
  const sprites = spriteIndex(assets);
  const backgroundRole = liveUsage?.find((role) => role.key === 'background');
  const bg = liveUsage
    ? (backgroundRole?.assetName ? sprites.get(slugify(backgroundRole.assetName)) : undefined)
    : design.scenery
      ? sprites.get('background')
      : undefined;

  return (
    <div className="logic-diagram" data-testid="logic-diagram">
      <p className="logic-diagram__summary">{design.summary}</p>
      {liveUsage && (
        <p className="muted logic-diagram__source" data-testid="logic-live-art-source">
          Artwork shown here is from the active build. Edit or refresh it in Assets.
        </p>
      )}

      <section className="logic-block">
        <h4 className="logic-block__label">Entities</h4>
        <div className="logic-entities">
          {design.entities.map((e) => (
            (() => {
              const usage = liveUsage ? roleForDesignEntity(liveUsage, e.name) : undefined;
              const assetName = usage?.assetName;
              const url = assetName ? sprites.get(slugify(assetName)) : undefined;
              const source = liveUsage
                ? usage?.renderMode === 'asset' && assetName
                  ? `Live asset: ${assetName}`
                  : usage?.renderMode === 'background-baked'
                    ? 'Rendered in the active background'
                    : usage?.renderMode === 'ui'
                      ? 'Rendered by game UI'
                      : usage?.renderMode === 'procedural'
                        ? 'Rendered by the active game'
                        : 'No separate visual in this build'
                : sprites.get(slugify(e.name))
                  ? 'Attached art — build to confirm'
                  : 'No attached visual yet';
              return (
                <EntityNode
                  key={e.name}
                  name={e.name}
                  role={e.role}
                  url={url ?? (!liveUsage ? sprites.get(slugify(e.name)) : undefined)}
                  source={source}
                  assetName={assetName ?? undefined}
                />
              );
            })()
          ))}
        </div>
      </section>

      <section className="logic-block">
        <h4 className="logic-block__label">Actions</h4>
        <div className="logic-verbs">
          {design.coreVerbs.length > 0 ? (
            design.coreVerbs.map((v) => (
              <span key={v} className="logic-verb" data-testid="logic-verb">
                {v}
              </span>
            ))
          ) : (
            <span className="muted">No verbs recorded.</span>
          )}
        </div>
        {design.controls && (
          <div className="logic-controls">
            <span className="logic-block__sublabel">Controls</span>
            <p>{design.controls}</p>
          </div>
        )}
      </section>

      <section className="logic-block">
        <h4 className="logic-block__label">Rules</h4>
        <ul className="logic-mechanics">
          {design.mechanics.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
        <div className="logic-grid2">
          <div className="logic-win" data-testid="logic-win">
            <span className="logic-block__sublabel">Win / lose</span>
            <p>{design.winCondition}</p>
          </div>
          {design.modeNotes && (
            <div className="logic-modenotes">
              <span className="logic-block__sublabel">Modes</span>
              <p>{design.modeNotes}</p>
            </div>
          )}
        </div>
      </section>

      {bg && (
        <section className="logic-block" data-testid="logic-bg">
          <h4 className="logic-block__label">Background</h4>
          <img className="logic-bg__img" src={bg} alt="" loading="lazy" />
        </section>
      )}
    </div>
  );
}
